import React, { type ChangeEvent, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Checkbox } from '@atlaskit/checkbox';
import Button from '@atlaskit/button/new';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { MemoizedInsightIcon } from '../cmdb-object-picker/insight-icon';
import type { CmdbObject } from '../../rest/cmdb-object-picker';
import messages from './messages';

export interface DropdownItem {
	object: CmdbObject;
	isSelected: boolean;
	metadata?: string[];
}

export interface ObjectsDropdownProps {
	items: DropdownItem[];
	displayedCount: number;
	searchQuery: string;
	onSearchChange: (query: string) => void;
	isLoading: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	selectedCount: number;
	allowedLimit: number;
	isAllSelected: boolean;
	isIndeterminate: boolean;
	onSelectAllChange: (isChecked: boolean) => void;
	onObjectSelectionChange: (objectId: string, isSelected: boolean) => void;
	/** Disables the confirm ("Add") button — true when the checked set is unchanged from the field. */
	isConfirmDisabled: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

const DropdownListItem = ({
	item,
	onSelectionChange,
	isDisabled,
}: {
	item: DropdownItem;
	onSelectionChange: (objectId: string, isSelected: boolean) => void;
	isDisabled: boolean;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	const handleCheckboxChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			onSelectionChange(item.object.id, event.target.checked);
		},
		[item.object.id, onSelectionChange],
	);

	const label = item.object.label;

	return (
		<Box xcss={listItemStyles}>
			<Inline space="space.100" alignBlock="center">
				<Checkbox
					isChecked={item.isSelected}
					onChange={handleCheckboxChange}
					aria-label={formatMessage(messages.selectObject, { objectLabel: label })}
					isDisabled={item.isSelected ? false : isDisabled}
				/>
				<Box xcss={iconContainerStyles}>
					<MemoizedInsightIcon
						{...item.object.avatar.mediaClientConfig}
						iconUrl={item.object.avatar.url48}
					/>
				</Box>
				<Stack space="space.0" grow="fill" xcss={textColumnStyles}>
					<Box xcss={objectLabelStyles}>
						<Text>{label}</Text>
					</Box>
					{item.metadata && item.metadata.length > 0 && (
						<Box xcss={metadataStyles}>
							<Text size="small" color="color.text.subtlest">
								{item.metadata.join(' | ')}
							</Text>
						</Box>
					)}
				</Stack>
			</Inline>
		</Box>
	);
};

export const ObjectsDropdown = ({
	items,
	displayedCount,
	searchQuery,
	onSearchChange,
	isLoading,
	hasMore,
	onLoadMore,
	selectedCount,
	allowedLimit,
	isAllSelected,
	isIndeterminate,
	onSelectAllChange,
	onObjectSelectionChange,
	isConfirmDisabled,
	onCancel,
	onConfirm,
}: ObjectsDropdownProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const listContainerRef = useRef<HTMLDivElement>(null);

	const handleSearchChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			onSearchChange(event.target.value);
		},
		[onSearchChange],
	);

	const handleScroll = useCallback(() => {
		const el = listContainerRef.current;
		if (!el || isLoading || !hasMore) {
			return;
		}
		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
			onLoadMore();
		}
	}, [isLoading, hasMore, onLoadMore]);

	useEffect(() => {
		const el = listContainerRef.current;
		if (!el) {
			return undefined;
		}
		el.addEventListener('scroll', handleScroll);
		return () => el.removeEventListener('scroll', handleScroll);
	}, [handleScroll]);

	const selectAllCount = displayedCount > allowedLimit ? allowedLimit : displayedCount;
	const isLimitReached = selectedCount >= allowedLimit;

	return (
		<Box xcss={containerStyles} testId="cmdb-object-picker-new.objects-dropdown">
			<Box xcss={headerStyles}>
				<Textfield
					appearance="standard"
					value={searchQuery}
					onChange={handleSearchChange}
					placeholder={formatMessage(messages.searchPlaceholder)}
					aria-label={formatMessage(messages.search)}
					autoFocus
					elemBeforeInput={
						<Box xcss={searchIconStyles}>
							<SearchIcon label="" size="small" />
						</Box>
					}
				/>
				<Box xcss={resultsInfoStyles}>
					<Inline space="space.050" alignBlock="center">
						<Text size="small">
							<Box as="span" role="status" aria-atomic="true">
								{formatMessage(messages.showingResults, { displayed: displayedCount })}
							</Box>
						</Text>
						{hasMore && (
							<Pressable xcss={loadMoreButtonStyles} onClick={onLoadMore}>
								{formatMessage(messages.loadMore)}
							</Pressable>
						)}
					</Inline>
				</Box>
			</Box>

			{displayedCount > 0 && (
				<Box xcss={selectAllContainerStyles}>
					<Checkbox
						isChecked={isAllSelected}
						isIndeterminate={isIndeterminate}
						onChange={(e: ChangeEvent<HTMLInputElement>) => onSelectAllChange(e.target.checked)}
						label={formatMessage(messages.selectFirstResults, { count: selectAllCount })}
						isDisabled={
							allowedLimit === 0 || (isLimitReached && !isIndeterminate && !isAllSelected)
						}
					/>
				</Box>
			)}

			<Box xcss={dividerStyles} />

			<Box xcss={listContainerStyles} ref={listContainerRef}>
				{items.map((item) => (
					<DropdownListItem
						key={item.object.id}
						item={item}
						onSelectionChange={onObjectSelectionChange}
						isDisabled={isLimitReached}
					/>
				))}
				{!isLoading && items.length === 0 && searchQuery && (
					<Box xcss={emptyStateStyles}>
						<Text size="small">{formatMessage(messages.noResults)}</Text>
					</Box>
				)}
				{isLoading && (
					<Box xcss={loadingContainerStyles}>
						<Spinner size="small" label={formatMessage(messages.loading)} />
					</Box>
				)}
			</Box>

			<Box xcss={dividerStyles} />

			<Box xcss={footerStyles}>
				{isLimitReached ? (
					<Inline space="space.050" alignBlock="center">
						<Box xcss={warningIconStyles}>
							<StatusWarningIcon label="" color={token('color.icon.warning', '#E06C00')} />
						</Box>
						<Text size="small">{formatMessage(messages.fieldLimitReached)}</Text>
					</Inline>
				) : (
					<Text size="small">
						{formatMessage(messages.selectedCount, { count: selectedCount })}
					</Text>
				)}
				<Inline space="space.100">
					<Button appearance="subtle" onClick={onCancel}>
						{formatMessage(messages.cancel)}
					</Button>
					<Button appearance="primary" onClick={onConfirm} isDisabled={isConfirmDisabled}>
						{formatMessage(messages.addObjects, { count: selectedCount })}
					</Button>
				</Inline>
			</Box>
		</Box>
	);
};

const containerStyles = xcss({
	width: '400px',
	minWidth: '400px',
	maxWidth: '600px',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
});

const headerStyles = xcss({
	padding: 'space.150',
});

const searchIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingInlineStart: 'space.050',
	color: 'color.text.subtle',
});

const resultsInfoStyles = xcss({
	marginTop: 'space.100',
	color: 'color.text.subtlest',
});

const selectAllContainerStyles = xcss({
	paddingInline: 'space.150',
	paddingBlock: 'space.050',
});

const dividerStyles = xcss({
	height: '1px',
	backgroundColor: 'color.background.neutral',
	flexShrink: 0,
});

const listContainerStyles = xcss({
	maxHeight: '240px',
	overflowY: 'auto',
	paddingBlock: 'space.050',
});

const listItemStyles = xcss({
	paddingInline: 'space.150',
	paddingBlock: 'space.075',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

const iconContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
});

const textColumnStyles = xcss({
	minWidth: 'space.0',
});

const objectLabelStyles = xcss({
	overflowWrap: 'anywhere',
	wordBreak: 'break-word',
});

const metadataStyles = xcss({
	overflowWrap: 'anywhere',
	wordBreak: 'break-word',
});

const emptyStateStyles = xcss({
	padding: 'space.150',
	color: 'color.text.subtlest',
	textAlign: 'center',
});

const loadingContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	padding: 'space.150',
});

const footerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: 'space.150',
});

const warningIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

// "Load more" rendered as an inline text link (mirrors Jira's ObjectsDropdown Pressable), avoiding button chrome.
const loadMoreButtonStyles = xcss({
	display: 'inline-flex',
	color: 'color.link',
	cursor: 'pointer',
	textDecoration: 'none',
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
	':hover': {
		textDecoration: 'underline',
	},
});
